import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Eye, Monitor, Activity, Brain, Settings,
  BarChart3, Shield, LogOut, ChevronLeft, ChevronRight, Menu, X, TrendingUp, Users,
  UserCog, KeyRound, FileText, Microscope, Timer } from 'lucide-react';
import { authAPI } from '../services/api';
import { getVersionString } from '../version';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import ChangePasswordModal from './ChangePasswordModal';

type AdminRole = 'sasadmin' | 'sys-operator';

interface NavItem {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  color: string;
  // sys-operator (customer-side restricted role) cannot see items where this is true.
  sasadminOnly?: boolean;
}

interface NavSection {
  titleKey: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    titleKey: 'nav.sectionMain',
    items: [
      { path: '/admin/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', color: 'text-blue-400' },
      { path: '/admin/members', icon: Users, labelKey: 'nav.memberManagement', color: 'text-violet-400' },
      { path: '/admin/captured-ads', icon: Eye, labelKey: 'nav.capturedAds', color: 'text-sky-400', sasadminOnly: true },
    ],
  },
  {
    titleKey: 'nav.sectionResearch',
    items: [
      { path: '/admin/research', icon: BarChart3, labelKey: 'nav.research', color: 'text-amber-400' },
      { path: '/admin/research/activity', icon: TrendingUp, labelKey: 'nav.researchActivity', color: 'text-teal-400' },
      { path: '/admin/reports', icon: FileText, labelKey: 'nav.reports', color: 'text-green-400' },
      { path: '/admin/gambling-analysis', icon: Shield, labelKey: 'nav.gamblingAnalysis', color: 'text-red-400', sasadminOnly: true },
      { path: '/admin/post-survey-analysis', icon: Microscope, labelKey: 'nav.postSurvey', color: 'text-fuchsia-400', sasadminOnly: true },
      { path: '/admin/display-time-verification', icon: Timer, labelKey: 'nav.displayTime', color: 'text-teal-400', sasadminOnly: true },
    ],
  },
  {
    titleKey: 'nav.sectionSystem',
    items: [
      { path: '/admin/system-monitor', icon: Monitor, labelKey: 'nav.systemMonitor', color: 'text-cyan-400' },
      { path: '/admin/admin-users', icon: UserCog, labelKey: 'nav.adminUsers', color: 'text-pink-400', sasadminOnly: true },
      { path: '/admin/enrichment-monitor', icon: Activity, labelKey: 'nav.enrichment', color: 'text-emerald-400', sasadminOnly: true },
      { path: '/admin/classifier-settings', icon: Brain, labelKey: 'nav.classifier', color: 'text-purple-400', sasadminOnly: true },
      { path: '/admin/pattern-settings', icon: Settings, labelKey: 'nav.patterns', color: 'text-indigo-400', sasadminOnly: true },
    ],
  },
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function AdminSidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [role, setRole] = useState<AdminRole>('sasadmin');
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authAPI.checkAdminAuth().then(res => {
      if (!cancelled && res.authenticated && res.user) {
        setRole((res.user.role ?? 'sasadmin') as AdminRole);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const visibleSections: NavSection[] = NAV_SECTIONS
    .map(section => ({
      ...section,
      items: section.items.filter(item => role === 'sasadmin' || !item.sasadminOnly),
    }))
    .filter(section => section.items.length > 0);

  async function handleLogout() {
    try {
      await authAPI.adminLogout();
      navigate('/admin');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  function handleNav(path: string) {
    navigate(path);
    onMobileClose();
  }

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo / Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700/50">
        {!collapsed && (
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-white truncate">SAS Admin</h2>
            <p className="text-[10px] text-gray-400 truncate">{getVersionString()}</p>
          </div>
        )}
        <button onClick={onToggle}
          className="p-1.5 rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors flex-shrink-0">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {visibleSections.map((section, si) => (
          <div key={si} className="mb-3">
            {!collapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                {t(section.titleKey)}
              </p>
            )}
            {collapsed && si > 0 && <div className="mx-3 mb-2 border-t border-gray-700/50" />}
            {section.items.map(item => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button key={item.path} onClick={() => handleNav(item.path)}
                  title={collapsed ? t(item.labelKey) : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2 mb-0.5 rounded-lg text-sm transition-all duration-150 ${
                    isActive
                      ? 'bg-white/10 text-white font-semibold'
                      : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                  } ${collapsed ? 'justify-center px-2' : ''}`}>
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? item.color : ''}`} />
                  {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom controls */}
      <div className="border-t border-gray-700/50 px-3 py-3 space-y-2">
        <div className={`flex items-center ${collapsed ? 'flex-col gap-2' : 'gap-2'}`}>
          <ThemeToggle />
          <LanguageSwitcher />
        </div>
        <button onClick={() => setPwOpen(true)}
          title={collapsed ? t('password.change') : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:bg-white/5 hover:text-gray-200 transition-colors ${collapsed ? 'justify-center px-2' : ''}`}>
          <KeyRound className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>{t('password.change')}</span>}
        </button>
        <button onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors ${collapsed ? 'justify-center px-2' : ''}`}>
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>{t('nav.logout')}</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40 bg-gray-900 dark:bg-gray-950 border-r border-gray-800 transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-56'
      }`}>
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onMobileClose} />
      )}

      {/* Mobile sidebar */}
      <aside className={`lg:hidden fixed left-0 top-0 bottom-0 z-50 w-64 bg-gray-900 dark:bg-gray-950 border-r border-gray-800 transition-transform duration-200 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="absolute top-4 right-4">
          <button onClick={onMobileClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {sidebarContent}
      </aside>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}

export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
      <Menu className="w-5 h-5" />
    </button>
  );
}
